import { AgencyFilterDto, CreateFieldBlockDto, FieldBlockDto, FieldBlockSchemaDto, PagedResponse } from "@bandido/shared/dtos";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, UseMutationOptions, useQuery } from "react-query";
import { QueryKeys } from "../common/types";
import { client } from "../common/services";

//TODO: pq todo extiende de AgencyFilterDTO #HambrePaManana
interface useGetFieldBlocksByAgencyProps extends AgencyFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
  agencyId?: string;
}

export const useGetFieldBlocksByAgency = (params?: useGetFieldBlocksByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getFieldBlocks, currentPage, pageSize, search, agencyId],
    queryFn: async (): Promise<PagedResponse<FieldBlockDto> | undefined> => {
      try {
        const endpoint = `custom-fields/agency/${agencyId}/field-block`;
        const users: AxiosResponse<PagedResponse<FieldBlockDto>, any> = await client.get(endpoint, {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(agencyId),
  });
};

interface UseGetFieldBlockByIdProps {
  id?: string;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetFieldBlockById = ({ id, onErrorCallback }: UseGetFieldBlockByIdProps) =>
  useQuery({
    queryKey: [QueryKeys.getFieldBlockById, id],
    queryFn: async (): Promise<FieldBlockDto | undefined> => {
      try {
        const result: AxiosResponse<FieldBlockDto, any> = await client.get(`custom-fields/field-block/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

// MUTATIONS
const patchCustomFieldBlock = async (data: CreateFieldBlockDto & { fieldBlockId?: string }) => {
  const { fieldBlockId } = data;
  const payload = {
    name: data.name,
    agencyId: data.agencyId,
    fields: data.fields,
    workflows: data.workflows
  }
  return client.patch<FieldBlockDto>(`custom-fields/field-block/${fieldBlockId}`, payload);

};

export const usePatchCustomFieldBlock = (
  options?:
    | Omit<
      UseMutationOptions<
        AxiosResponse<FieldBlockDto, any>,
        unknown,
        CreateFieldBlockDto & { fieldBlockId?: string },
        unknown
      >,
      'mutationFn'
    >
    | undefined,
) => useMutation((data) => patchCustomFieldBlock(data), options);

interface UseGetFieldBlockByWorkflowIdProps {
  id?: string;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetFieldBlockByWorkflowId = ({ id, onErrorCallback }: UseGetFieldBlockByWorkflowIdProps) =>
  useQuery({
    queryKey: [QueryKeys.getFieldBlockByWorkflowId, id],
    queryFn: async (): Promise<FieldBlockSchemaDto[] | undefined> => {
      try {
        const result: AxiosResponse<FieldBlockSchemaDto[], any> = await client.get(`custom-fields/field-block/workflow/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

  interface UseGetFieldBlockByWorkflowIdsProps {
    workflows?: string;
    onErrorCallback?: (_e: AxiosError) => void;
  }

  export const useGetFieldBlockByWorkflowIds = ({ workflows, onErrorCallback }: UseGetFieldBlockByWorkflowIdsProps) =>
    useQuery({
      queryKey: [QueryKeys.getFieldBlockByWorkflowIds, workflows],
      queryFn: async (): Promise<FieldBlockSchemaDto[] | undefined> => {
        try {
          const result: AxiosResponse<FieldBlockSchemaDto[], any> = await client.get(`custom-fields/field-block`, {
            params: {
              workflowIds: workflows
            }
          });
  
          return result.data;
        } catch (e) {
          onErrorCallback && onErrorCallback(e as AxiosError);
        }
      },
      refetchOnWindowFocus: true,
      enabled: Boolean(workflows),
    });
